import { isPlatformServer } from "@angular/common";
import {
  AfterViewInit,
  ChangeDetectorRef,
  OnDestroy,
  Component,
  Inject,
  PLATFORM_ID,
  Output,
  EventEmitter
} from "@angular/core";
import { CKEditorLoadingState, CKEditorService } from "@core/services/ckeditor.service";
import { HighlightService } from "@core/services/highlight.service";
import { UtilsService } from "@core/services/utils/utils.service";
import { WindowRefService } from "@core/services/window-ref.service";
import { FieldType } from "@ngx-formly/core";
import { TranslateService } from "@ngx-translate/core";
import { Subscription } from "rxjs";

@Component({
  selector: "astrobin-formly-field-ckeditor",
  templateUrl: "./formly-field-ckeditor.component.html",
  styleUrls: ["./formly-field-ckeditor.component.scss"]
})
export class FormlyFieldCKEditorComponent extends FieldType implements AfterViewInit, OnDestroy {
  editor: any;
  showEditor = false;
  isLoading = true;
  hasError = false;
  isMaximized = false;
  activeButtons: { [key: string]: boolean } = {};
  isRunningInCypress = false;

  @Output() loadingStateChange = new EventEmitter<CKEditorLoadingState>();

  /**
   * Get the loading state change handler from props if available
   */
  get loadingStateChangeHandler(): ((state: CKEditorLoadingState) => void) | null {
    return this.field?.props?.loadingStateChange || null;
  }

  /**
   * Check if the editor should be in read-only state
   */
  get isReadOnly(): boolean {
    return this.formControl.disabled;
  }

  private _subscriptions: Subscription[] = [];
  private _loadRetries = 0;
  private _createRetries = 0;
  private readonly _maxRetries = 3;
  private readonly _maxCreateRetries = 5;

  constructor(
    public readonly translateService: TranslateService,
    public readonly ckeditorService: CKEditorService,
    public readonly utilsService: UtilsService,
    public readonly windowRefService: WindowRefService,
    public readonly highlightService: HighlightService,
    @Inject(PLATFORM_ID) public readonly platformId: Object,
    public readonly changeDetectorRef: ChangeDetectorRef
  ) {
    super();

    // Check if we're running in Cypress
    if (!isPlatformServer(this.platformId)) {
      this.isRunningInCypress = !!(this.windowRefService.nativeWindow as any).Cypress;
    }
  }

  ngAfterViewInit(): void {
    // Skip initialization on the server side
    if (isPlatformServer(this.platformId)) {
      return;
    }

    // If running in Cypress, just show a simple textarea
    if (this.isRunningInCypress) {
      this.isLoading = false;
      this.showEditor = true;
      this.hasError = false;

      // Emit ready state immediately
      this.loadingStateChange.emit(CKEditorLoadingState.READY);
      if (this.loadingStateChangeHandler) {
        this.loadingStateChangeHandler(CKEditorLoadingState.READY);
      }

      this.changeDetectorRef.detectChanges();
      return;
    }

    // Wait a small amount of time to ensure DOM is ready
    this.utilsService.delay(50).subscribe(() => {
      this._initializeEditor();
    });
  }

  ngOnDestroy(): void {
    // Clean up subscriptions
    this._subscriptions.forEach(sub => sub.unsubscribe());
    this._subscriptions = [];

    // Skip CKEDITOR cleanup on the server side
    if (isPlatformServer(this.platformId)) {
      return;
    }

    // Destroy the editor instance if it exists
    const win = this.windowRefService.nativeWindow as any;
    if (this.field?.id && win.CKEDITOR && typeof win.CKEDITOR.instances?.[this.field.id] !== "undefined") {
      try {
        win.CKEDITOR.instances[this.field.id].destroy();
      } catch (error) {
        console.warn("Error destroying CKEditor instance:", error);
      }
    }
  }

  /**
   * Update the placeholder with a new random one when content is cleared
   */
  private _updatePlaceholder(): void {
    if (!this.editor || !this.field?.props?.placeholders) {
      return;
    }

    const placeholders = this.field.props.placeholders;
    if (!Array.isArray(placeholders) || placeholders.length === 0) {
      return;
    }

    // Get a random placeholder
    const newPlaceholder = placeholders[Math.floor(Math.random() * placeholders.length)];

    // Update the placeholder in the editor
    try {
      // Update the editor config
      if (this.editor.config) {
        this.editor.config.editorplaceholder = newPlaceholder;
      }

      // Update the data attribute on the editable element
      if (this.editor.editable && this.editor.editable()) {
        const editorElement = this.editor.editable().$;
        if (editorElement) {
          editorElement.setAttribute("data-placeholder", newPlaceholder);
        }
      }
    } catch (error) {
      console.warn("Error updating placeholder:", error);
    }
  }

  /**
   * Execute CKEditor command from custom toolbar buttons
   * @param command The CKEditor command name
   */
  execCommand(command: string): void {
    if (isPlatformServer(this.platformId) || !this.editor || this.isReadOnly) {
      return;
    }

    try {
      // Handle special cases that need custom handling
      if (command.startsWith("fontsize:")) {
        const size = command.split(":")[1];

        // Focus the editor and prepare for style application
        this.editor.focus();
        this.editor.fire("saveSnapshot");

        // Access the global CKEDITOR
        const win = this.windowRefService.nativeWindow as any;
        const CKEDITOR = win.CKEDITOR;

        if (!CKEDITOR) {
          console.warn("CKEDITOR global not available");
          return;
        }

        // Create a style object directly from the global CKEDITOR
        const style = new CKEDITOR.style({
          element: "span",
          styles: { "font-size": size },
          overrides: [{ element: "font", attributes: { size: null } }]
        });

        // Apply the style
        this.editor.applyStyle(style);

        // Save snapshot for undo history
        this.editor.fire("saveSnapshot");
        return;
      } else if (command === "codeSnippet") {
        // Make sure highlight.js is loaded before executing the command
        this.highlightService
          .loadHighlightJs()
          .then(() => {
            // Now that highlight.js is loaded, execute the command
            this.editor.focus();

            // Try to execute the command directly
            try {
              this.editor.execCommand("codeSnippet");
            } catch (error) {
              console.warn("Error executing codeSnippet command:", error);

              // If direct command execution fails, try to open the dialog manually
              const win = this.windowRefService.nativeWindow as any;
              if (win.CKEDITOR && win.CKEDITOR.dialog) {
                try {
                  win.CKEDITOR.dialog.open("codeSnippet", this.editor);
                } catch (dialogError) {
                  console.error("Failed to open code snippet dialog:", dialogError);
                }
              }
            }
          })
          .catch(error => {
            console.error("Failed to load highlight.js:", error);
          });
        return;
      } else if (command === "maximize") {
        // Execute the maximize command
        this.editor.execCommand("maximize");

        // Update the maximized state - we don't need to toggle it here
        // as there's a listener on the maximizeCommand state that will
        // update isMaximized automatically

        // Force a detection cycle to update the UI
        this.changeDetectorRef.detectChanges();
        return;
      }

      // For standard commands
      this.editor.execCommand(command);

      // Update active state for buttons (for commands that toggle on/off)
      const toggleCommands = ["bold", "italic", "underline", "strike"];
      if (toggleCommands.includes(command)) {
        this.activeButtons[command] = this.editor.getCommand(command).state === 1;
        this.changeDetectorRef.detectChanges();
      }

      // Set focus back to editor after command execution
      this.editor.focus();
    } catch (error) {
      console.warn(`Error executing CKEditor command '${command}':`, error);
    }
  }

  /**
   * Public method to reinitialize the editor
   * This is exposed for the retry button in the UI
   */
  _initializeEditor(): void {
    if (isPlatformServer(this.platformId)) {
      return;
    }

    // Reset state
    this.isLoading = true;
    this.hasError = false;
    this.showEditor = false;
    this._loadRetries = 0;
    this._createRetries = 0;

    // Clean up existing subscriptions to avoid duplicate handlers
    this._subscriptions.forEach(sub => sub.unsubscribe());
    this._subscriptions = [];

    // Clean up any existing instance
    const win = this.windowRefService.nativeWindow as any;
    if (this.field?.id && win.CKEDITOR && typeof win.CKEDITOR.instances?.[this.field.id] !== "undefined") {
      try {
        win.CKEDITOR.instances[this.field.id].destroy();
      } catch (error) {
        console.warn("Error destroying existing CKEditor instance:", error);
      }
    }

    // Subscribe to the loadingState$ observable to track CKEditor loading state
    this._subscriptions.push(
      this.ckeditorService.loadingState$.subscribe(state => {
        switch (state) {
          case CKEditorLoadingState.LOADING:
            this.isLoading = true;
            this.hasError = false;
            this.showEditor = false;
            // Emit loading state change
            this.loadingStateChange.emit(state);

            // Call handler from props if available
            if (this.loadingStateChangeHandler) {
              this.loadingStateChangeHandler(state);
            }
            break;

          case CKEditorLoadingState.READY:
            // CKEditor is fully loaded, create the editor instance
            this._createEditorInstance();
            break;

          case CKEditorLoadingState.ERROR:
            // CKEditor failed to load
            this.isLoading = false;
            this.hasError = true;
            this.showEditor = false;
            // Emit loading state change
            this.loadingStateChange.emit(state);

            // Call handler from props if available
            if (this.loadingStateChangeHandler) {
              this.loadingStateChangeHandler(state);
            }
            this.changeDetectorRef.detectChanges();
            break;
        }
      })
    );

    // Start loading CKEditor
    this.ckeditorService.loadCKEditor().catch(error => {
      console.error("Error loading CKEditor:", error);
      this.isLoading = false;
      this.hasError = true;
      this.changeDetectorRef.detectChanges();
    });

    this.changeDetectorRef.detectChanges();
  }

  /**
   * Shows the editor when it's ready
   */
  private _showEditor(): void {
    if (isPlatformServer(this.platformId)) {
      return;
    }

    // First check if the editor UI is actually available before showing it
    const checkEditorUIReady = () => {
      // Check for the presence of the editor container and toolbar
      const editorContainer = this.windowRefService.nativeWindow.document.querySelector(`#cke_${this.field.id}`);

      const editorContent = this.windowRefService.nativeWindow.document.querySelector(
        `#cke_${this.field.id} .cke_wysiwyg_div`
      );

      const editorToolbar = this.windowRefService.nativeWindow.document.querySelector(`#cke_${this.field.id} .cke_top`);

      // Only show the editor when both container and content area are ready
      if (editorContainer && editorContent && editorToolbar) {
        // UI is ready, now we can show the editor and hide the loading indicator
        this.isLoading = false;
        this.showEditor = true;
        // Emit ready state now that the editor UI is fully ready
        this.loadingStateChange.emit(CKEditorLoadingState.READY);

        // Call handler from props if available
        if (this.loadingStateChangeHandler) {
          this.loadingStateChangeHandler(CKEditorLoadingState.READY);
        }
        this.changeDetectorRef.detectChanges();

        // Set minimum height for the editor content area
        try {
          editorContent["style"].minHeight = `${this.field?.props?.height || 300}px`;
        } catch (error) {
          console.warn("Error setting editor height:", error);
        }

        return true;
      }

      return false;
    };

    // If the UI isn't ready yet, set up a polling mechanism
    if (!checkEditorUIReady()) {
      // Only proceed with polling in browser context
      if (isPlatformServer(this.platformId)) {
        // In SSR, just show the editor (it will be properly rendered client-side)
        this.isLoading = false;
        this.showEditor = true;
        return;
      }

      let attempts = 0;
      const maxAttempts = 50; // Check for up to 5 seconds (50 * 100ms)

      // Use a recursive pattern with utilsService.delay
      const pollEditorUI = () => {
        attempts++;

        if (checkEditorUIReady()) {
          // Editor UI is ready, no need to continue polling
          return;
        }

        if (attempts >= maxAttempts) {
          // We've reached max attempts, just show the editor anyway
          console.warn("Timed out waiting for CKEditor UI to be ready");
          this.isLoading = false;
          this.showEditor = true;
          this.changeDetectorRef.detectChanges();
          return;
        }

        // Use a single delay, then call this function again (recursively)
        this.utilsService.delay(100).subscribe(() => pollEditorUI());
      };

      // Start polling with initial delay
      this.utilsService.delay(100).subscribe(() => pollEditorUI());
    }
  }

  /**
   * Create the CKEditor instance once CKEditor is loaded
   */
  private _createEditorInstance(): void {
    if (isPlatformServer(this.platformId)) {
      return;
    }

    // Get the DOM element for the editor
    const document = this.windowRefService.nativeWindow.document;
    const editorBase = document.getElementById(this.field.id);
    const win = this.windowRefService.nativeWindow as any;

    if (!editorBase) {
      console.error("Editor base element not found");
      this.isLoading = false;
      this.hasError = true;
      this.changeDetectorRef.detectChanges();
      return;
    }

    if (!win.CKEDITOR) {
      console.error("CKEDITOR is not available");
      this.isLoading = false;
      this.hasError = true;
      this.changeDetectorRef.detectChanges();
      return;
    }

    // Preload highlight.js for code snippets, even if not immediately used
    // This ensures it's available when the editor loads with existing content
    this.highlightService.loadHighlightJs().catch(error => {
      console.warn("Failed to preload highlight.js, code snippets may not be properly highlighted:", error);
    });

    try {
      // Use the service's centralized readiness check for editor creation
      if (!this.ckeditorService.isCKEditorReady(true)) {
        this._createRetries++;
        if (this._createRetries >= this._maxCreateRetries) {
          console.error("CKEditor failed to become ready for instance creation after maximum retries");
          this.isLoading = false;
          this.hasError = true;
          this.changeDetectorRef.detectChanges();
          return;
        }
        console.warn(
          `CKEditor not ready for instance creation, retrying... (${this._createRetries}/${this._maxCreateRetries})`
        );
        this.utilsService.delay(500).subscribe(() => this._createEditorInstance());
        return;
      }

      // Get editor options
      const editorOptions = this.ckeditorService.options(this.formControl);

      // Check if there's a placeholder in the field props and add it to options
      if (this.field?.props?.placeholder) {
        editorOptions.editorplaceholder = this.field.props.placeholder;
      }

      // Set up error handler for the editor
      if (typeof win.CKEDITOR.on === "function" && !win.CKEDITOR._errorHandlerAdded) {
        win.CKEDITOR.on("instanceCreated", event => {
          event.editor.on("error", errorEvent => {
            console.warn("CKEditor instance error intercepted:", errorEvent.data);
            errorEvent.cancel(); // Prevent errors from stopping editor functionality
          });
        });
        win.CKEDITOR._errorHandlerAdded = true;
      }

      // Try to create the editor instance
      try {
        // Setup fallback for BBCode plugin errors
        let useBBCode = true;
        let attempts = 0;

        const createInstance = () => {
          attempts++;

          try {
            // Modify options if we need to disable BBCode
            const options = useBBCode
              ? editorOptions
              : {
                  ...editorOptions,
                  extraPlugins: editorOptions.extraPlugins.replace("bbcode,", "")
                };

            // Create the editor instance
            this.editor = win.CKEDITOR.replace(this.field.id, options);

            if (this.editor) {
              // Set initial content
              if (this.formControl && this.formControl.value) {
                this.editor.setData(this.formControl.value);
              }

              // Listen for the instanceReady event
              this.editor.on("instanceReady", () => {
                console.log("CKEditor instance ready");

                // Apply placeholder styling if needed
                if (this.field?.props?.placeholder && this.editor.editable()) {
                  const editorElement = this.editor.editable().$;

                  // Add a data attribute to help with styling
                  if (editorElement) {
                    editorElement.setAttribute("data-placeholder", this.field.props.placeholder);
                  }
                }

                // Check if initial content has blockquotes after a small delay
                this.utilsService.delay(100).subscribe(() => {
                  const content = this.editor.getData();
                  if (content && content.trim()) {
                    // Check if content ends with a quote block in BBCode
                    const trimmedContent = content.trim();
                    if (trimmedContent.endsWith("[/quote]")) {
                      // Fire the afterCommandExec event to ensure paragraph after blockquote
                      this.editor.fire("afterCommandExec", {
                        name: "blockquote",
                        command: { name: "blockquote" }
                      });
                    }
                  }
                });

                // Apply read-only state based on form control disabled state
                this.editor.setReadOnly(this.isReadOnly);

                // Subscribe to form control status changes (enabled/disabled)
                this._subscriptions.push(
                  this.formControl.statusChanges.subscribe(() => {
                    this.editor.setReadOnly(this.isReadOnly);
                    this.changeDetectorRef.markForCheck();
                  })
                );

                // Subscribe to form control value changes to sync with editor
                this._subscriptions.push(
                  this.formControl.valueChanges.subscribe(value => {
                    // Only update if the editor's current data is different
                    // to avoid infinite loops
                    const currentData = this.editor.getData();
                    if (currentData !== value) {
                      this.editor.setData(value || "");

                      // If the content is now empty, update the placeholder
                      if (!value || value.trim() === "") {
                        this._updatePlaceholder();
                      }
                    }
                  })
                );

                // Also listen to editor content changes to update placeholder when user deletes all content
                this.editor.on("change", () => {
                  const content = this.editor.getData();
                  if (!content || content.trim() === "") {
                    this._updatePlaceholder();
                  }
                });

                // Update placeholder on blur event
                this.editor.on("blur", () => {
                  this._updatePlaceholder();
                });

                // Listen for the maximize command to update our isMaximized state
                const maximizeCommand = this.editor.getCommand("maximize");
                if (maximizeCommand) {
                  maximizeCommand.on("state", () => {
                    // State 1 is maximized, 2 is minimized/normal
                    this.isMaximized = maximizeCommand.state === 1;
                    this.changeDetectorRef.detectChanges();
                  });
                }

                // Add blockquote fix: automatically insert paragraph after blockquote
                this.editor.on("afterCommandExec", e => {
                  if (e.data.name === "blockquote") {
                    const selection = this.editor.getSelection();
                    const range = selection.getRanges()[0];

                    if (range) {
                      // Get the current element - should be inside the blockquote
                      const currentElement = range.startContainer;

                      // Find the blockquote element by traversing up the DOM
                      let blockquoteElement = currentElement;
                      while (
                        blockquoteElement &&
                        blockquoteElement.getName &&
                        blockquoteElement.getName() !== "blockquote"
                      ) {
                        blockquoteElement = blockquoteElement.getParent();
                      }

                      if (
                        blockquoteElement &&
                        blockquoteElement.getName &&
                        blockquoteElement.getName() === "blockquote"
                      ) {
                        // Create a new paragraph element
                        const newParagraph = new win.CKEDITOR.dom.element("p");
                        newParagraph.setHtml("&nbsp;");

                        // Insert the paragraph after the blockquote
                        newParagraph.insertAfter(blockquoteElement);

                        // Move cursor into the new paragraph
                        const newRange = this.editor.createRange();
                        newRange.moveToPosition(newParagraph, win.CKEDITOR.POSITION_AFTER_START);
                        this.editor.getSelection().selectRanges([newRange]);

                        // Focus the editor to ensure cursor is visible
                        this.editor.focus();
                      }
                    }
                  }
                });

                // Check if the editor has content with code snippets and apply highlighting
                const content = this.editor.getData();
                if (content && this.highlightService.needsHighlighting(content)) {
                  // Wait for the editor to fully render its content
                  this.utilsService.delay(100).subscribe(() => {
                    const editorFrame = this.windowRefService.nativeWindow.document.querySelector(
                      `#cke_${this.field.id} iframe`
                    ) as HTMLIFrameElement;

                    if (editorFrame && editorFrame.contentDocument) {
                      // Apply highlighting to code blocks within the editor iframe
                      this.highlightService.highlightCodeBlocks(editorFrame.contentDocument.body);
                    }
                  });
                }

                this._showEditor();
              });

              // Also start showing the editor
              // The _showEditor method is safe to call multiple times
              this._showEditor();
            } else {
              throw new Error("Failed to create editor instance");
            }
          } catch (error) {
            console.error("Error creating CKEditor instance:", error);
            const errorStr = error.toString();

            // Check if this is a BBCode-related error
            const isBBCodeError =
              errorStr.includes("fragment") ||
              errorStr.includes("htmlParser") ||
              errorStr.includes("extend") ||
              errorStr.includes("filter");

            // If it's a BBCode error and we haven't tried without BBCode yet, retry without BBCode
            if (isBBCodeError && useBBCode && attempts <= 2) {
              console.log("Retrying without BBCode plugin");
              useBBCode = false;
              createInstance();
            } else if (attempts <= this._maxRetries) {
              // Try again with a delay - use utilsService.delay for SSR-safe delay
              this.utilsService.delay(500).subscribe(() => createInstance());
            } else {
              // Give up after max retries
              this.isLoading = false;
              this.hasError = true;
              this.changeDetectorRef.detectChanges();
            }
          }
        };

        // Start the creation process
        createInstance();
      } catch (error) {
        console.error("Unexpected error creating editor:", error);
        this.isLoading = false;
        this.hasError = true;
        this.changeDetectorRef.detectChanges();
      }
    } catch (error) {
      console.error("Error preparing editor options:", error);
      this.isLoading = false;
      this.hasError = true;
      this.changeDetectorRef.detectChanges();
    }
  }
}
